import React, { useState, useEffect } from 'react';
import withWidth from '@material-ui/core/withWidth';
import Dialog from '@material-ui/core/Dialog';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import Table from '@material-ui/core/Table';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import Checkbox from '@material-ui/core/Checkbox';

import { ButtonCount, Snackbar } from 'components';
import { useForm, Controller } from 'react-hook-form';
import { useSnackbar } from 'hooks';
import api from 'services/api';

import useStyles from './styles';
import { formatMoney } from 'helpers/moneyFormatter';
import { useWaiterTable } from 'pages/garcom/tables/useWaiterTable';

const ProductDialog = ({
  open,
  handleClose = () => {},
  productId,
  width,
  restaurantId,
}) => {
  const [product, setProduct] = useState({});
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState(1);
  const [additionals, setAdditionals] = useState([]);
  const { openSnackbar } = useSnackbar();
  const classes = useStyles();
  const { addProductToRequest } = useWaiterTable();

  const getProduct = async () => {
    setLoading(true);
    try {
      const product = await api.get(`/products/${productId}`);
      const productOption = await api.get(`/products/${productId}/options`);

      setProduct({
        ...product.data.object,
        options: productOption.data.object,
      });
      // eslint-disable-next-line no-unused-vars
    } catch (err) {
      openSnackbar('Não foi possível buscar o produto.', 'error');
    }
    setLoading(false);
  };

  useEffect(() => {
    if (open) {
      getProduct();
    }
  }, [open]);

  const { register, control, handleSubmit } = useForm();

  const onSubmit = async ({ note }) => {
    const newProduct = {
      ...product,
      cart: {
        product_id: product.id,
        restaurant_id: restaurantId,
        amount: amount,
        note,
        product_options_request_products:
          additionals.map((additional) => ({
            product_option_id: additional.id,
            request_product_id: product.id,
            price: additional.price,
            amount: additional.amount,
          })) ?? [],
      },
    };

    addProductToRequest(newProduct);
    handleClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        fullScreen={width === 'xs'}
        className={classes.dialog}
      >
        {loading ? (
          <Box
            display="flex"
            height="70vh"
            width="100%"
            justifyContent="center"
            alignItems="center"
          >
            <CircularProgress />
          </Box>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box
              className={classes.media}
              style={{ backgroundImage: `url(${product.image})` }}
            />
            <Box p={2}>
              <Typography gutterBottom variant="h5">
                {product.name}
              </Typography>
              <Typography gutterBottom variant="body1">
                {product.description}
              </Typography>
              <Box mb={2}>
                <ButtonCount count={amount} setCount={setAmount} />
              </Box>
              <Typography gutterBottom variant="caption">
                Observação
              </Typography>
              <Controller
                name="note"
                as={
                  <TextField
                    placeholder="Por exemplo: Tirar Cebola ..."
                    name="note"
                    variant="outlined"
                    margin="none"
                    inputRef={register}
                    fullWidth
                    multiline
                    minRows={3}
                    maxRows={6}
                  />
                }
                defaultValue=""
                control={control}
              />
              {product.options.map((option, optionIndex) => (
                <>
                  {option?.category_product_option?.product_options?.length >
                    0 && (
                    <Box mt={2} key={option.id}>
                      <Box
                        className={classes.optionsHeader}
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                      >
                        <Box>
                          <Typography
                            variant="body1"
                            className={classes.optionsHeaderText}
                          >
                            {option.category_product_option.name}
                          </Typography>
                          {option.category_product_option.input !==
                            'checkbox' && (
                            <Typography variant="caption">
                              Selecione apenas 1
                            </Typography>
                          )}
                        </Box>
                        {option.category_product_option.required && (
                          <Chip
                            size="small"
                            label="Obrigatório"
                            className={classes.optionsChip}
                          />
                        )}
                      </Box>
                      <Table>
                        {option?.category_product_option?.product_options?.map(
                          (productOption, productOptionIndex) => (
                            <TableRow key={productOption.id} role="checkbox">
                              <TableCell>{productOption.name}</TableCell>
                              <TableCell>
                                {formatMoney(productOption.price)}
                              </TableCell>
                              <TableCell align="right">
                                <Controller
                                  name={`options[${optionIndex}].productOptions[${productOptionIndex}]`}
                                  render={({ onChange, value, ...rest }) => {
                                    const handleChange = ({ target }) => {
                                      onChange(target.checked);

                                      if (target.checked) {
                                        setAdditionals((prevState) => [
                                          ...prevState,
                                          {
                                            ...productOption,
                                            amount: 1,
                                          },
                                        ]);
                                        return;
                                      }

                                      setAdditionals((prevState) => {
                                        return prevState.filter(
                                          (selectedOption) =>
                                            selectedOption.id !==
                                            productOption.id,
                                        );
                                      });
                                    };

                                    return (
                                      <Checkbox
                                        {...rest}
                                        onChange={handleChange}
                                        checked={value}
                                      />
                                    );
                                  }}
                                  defaultValue={false}
                                  control={control}
                                />
                              </TableCell>
                            </TableRow>
                          ),
                        )}
                      </Table>
                    </Box>
                  )}
                </>
              ))}
              <Box mt={2}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Button
                      color="secondary"
                      fullWidth
                      variant="outlined"
                      onClick={handleClose}
                    >
                      Voltar
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button
                      type="submit"
                      color="primary"
                      fullWidth
                      variant="contained"
                    >
                      Adicionar
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </form>
        )}
      </Dialog>

      <Snackbar />
    </>
  );
};

export default withWidth()(ProductDialog);
